import React, { useState, useEffect } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import "./DirectSearch.css";
import { stateCities } from "../constants/stateCities";
import { specialities } from "../constants/specialities";

const HcpSearchApp = () => {
    const { instance, accounts } = useMsal();
    const [userEmail, setUserEmail] = useState('');
  
    useEffect(() => {
      if (accounts.length > 0) {
        // Debug: Log the full account object to see all available properties
        console.log("MSAL Account Object:", accounts[0]);
        
        // Try different possible email fields
        const potentialEmail = 
          accounts[0].idTokenClaims?.email || 
          accounts[0].idTokenClaims?.preferred_username || 
          accounts[0].username || 
          '';
        
        console.log("Extracted email:", potentialEmail);
        setUserEmail(potentialEmail);
      }
    }, [accounts]);

    const [formData, setFormData] = useState({
        npi: "",
        firstName: "",
        lastName: "",
        address: "",
        state: "",
        city: "",
        mailingCity: "",
        mailingState: "",
        mailingAddress: "",
        licenseState: "",
        licenseNumber: "",
        specialty: "",
    });
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [popupRowIndex, setPopupRowIndex] = useState(null);
    const [cities, setCities] = useState([]);
    const [mailingCities, setMailingCities] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleStateChange = (e) => {
        const state = e.target.value;
        setFormData(prev => ({ ...prev, state }));
        setCities(stateCities[state] || []);
    };

    const handleMailingStateChange = (e) => {
        const mailingState = e.target.value;
        setFormData(prev => ({ ...prev, mailingState }));
        setMailingCities(stateCities[mailingState] || []);
    };

    const handleLicenseStateChange = (e) => {
        const licenseState = e.target.value;
        setFormData(prev => ({ ...prev, licenseState }));
    };

    const handleSearch = async (page = 1) => {
        const isAllFieldsEmpty = Object.entries(formData).every(
            ([key, value]) => !value || value.trim() === ""
        );
    
        if (isAllFieldsEmpty) {
            setErrorMessage("Please fill at least one field");
            return;
        }
    
        setErrorMessage("");
        setLoading(true);
    
        try {
            // Prepare search parameters
            const searchParams = Object.fromEntries(
                Object.entries(formData).filter(([_, value]) => value && value.trim() !== "")
            );
    
            // Store search hit
            await axios.post(`${process.env.REACT_APP_newBACKEND_URL}/api/hcpsearch/hits`, {
                searchParams,
                searchType: 'direct',
                userEmail: userEmail
            });
    
            // Get search results
            const response = await axios.get(`${process.env.REACT_APP_newBACKEND_URL}/api/hcpsearch`, { 
                params: {
                    ...searchParams,
                    page: parseInt(page),
                    limit: 10
                }
            });
            
            if (response.data?.results) {
                setResults(response.data.results);
                setTotalResults(response.data.total);
                setTotalPages(Math.ceil(response.data.total / 10));
                setCurrentPage(page);
            } else {
                setResults([]);
                setTotalResults(0);
            }
        } catch (error) {
            console.error("Search error:", error);
            setErrorMessage("Search failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            handleSearch(newPage);
        }
    };

    const handleView = async (record, index) => {
        if (popupRowIndex === index) {
            setPopupRowIndex(null);
            setSelectedRecord(null);
        } else {
            setPopupRowIndex(index);
            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_newBACKEND_URL}/api/hcpsearch/view/${record.NPI}`
                );
                setSelectedRecord(data.record);
                console.log(data.record);
            } catch (err) {
                console.error("Error fetching details:", err);
            }
        }
    };
    const clearForm = () => {
        setFormData({
            npi: "",
            firstName: "",
            lastName: "",
            address: "",
            state: "",
            city: "",
            mailingCity: "",
            mailingState: "",
            mailingAddress: "",
            licenseState: "",
            licenseNumber: "",
            specialty: "",
        });
        setResults([]);
        setCurrentPage(1);
        setTotalResults(0);
        setErrorMessage("");
    };
    return (
        <div className="direct-search-container">
            <header className="header">
                <h1>HCP Search</h1>
            </header>

            {errorMessage && (
                <div className="error-message">
                    <p>{errorMessage}</p>
                </div>
            )}

            <div className="search-form">
                <div className="form-row">
                    <div className="form-field">
                        <label>First Name</label>
                        <input
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Enter First Name"
                            disabled={loading}
                        />
                    </div>
                    <div className="form-field">
                        <label>Last Name</label>
                        <input
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Enter Last Name"
                            disabled={loading}
                        />
                    </div>
                    <div className="form-field">
                        <label>NPI</label>
                        <input
                            name="npi"
                            value={formData.npi}
                            onChange={handleInputChange}
                            placeholder="Enter NPI"
                            disabled={loading}
                            inputMode="numeric"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>State</label>
                        <select
                            name="state"
                            value={formData.state}
                            onChange={handleStateChange}
                            disabled={loading}
                        >
                            <option value="">Select State</option>
                            {Object.keys(stateCities).map((state) => (
                                <option key={state} value={state}>
                                    {state}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-field">
                        <label>City</label>
                        <select
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            disabled={loading}
                        >
                            <option value="">Select City</option>
                            {cities.map((city, index) => (
                                <option key={index} value={city}>
                                    {city}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-field">
                        <label>Address</label>
                        <input
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            placeholder="Enter Address"
                            disabled={loading}
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>Specialty</label>
                        <select
                            name="specialty"
                            value={formData.specialty}
                            onChange={handleInputChange}
                            disabled={loading}
                        >
                            <option value="">Select Specialty</option>
                            {specialities.map((speciality, index) => (
                                <option key={index} value={speciality}>
                                    {speciality}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-field">
                        <label>Mailing State</label>
                        <select
                            name="mailingState"
                            value={formData.mailingState}
                            onChange={handleMailingStateChange}
                            disabled={loading}
                        >
                            <option value="">Select Mailing State</option>
                            {Object.keys(stateCities).map((state) => (
                                <option key={state} value={state}>
                                    {state}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-field">
                        <label>Mailing City</label>
                        <select
                            name="mailingCity"
                            value={formData.mailingCity}
                            onChange={handleInputChange}
                            disabled={loading}
                        >
                            <option value="">Select Mailing City</option>
                            {mailingCities.map((city, index) => (
                                <option key={index} value={city}>
                                    {city}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>Mailing Address</label>
                        <input
                            name="mailingAddress"
                            value={formData.mailingAddress}
                            onChange={handleInputChange}
                            placeholder="Enter Mailing Address"
                            disabled={loading}
                        />
                    </div>
                    <div className="form-field">
                        <label>License State</label>
                        <select
                            name="licenseState"
                            value={formData.licenseState}
                            onChange={handleLicenseStateChange}
                            disabled={loading}
                        >
                            <option value="">Select License State</option>
                            {Object.keys(stateCities).map((state) => (
                                <option key={state} value={state}>
                                    {state}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-field">
                        <label>License Number</label>
                        <input
                            name="licenseNumber"
                            value={formData.licenseNumber}
                            onChange={handleInputChange}
                            placeholder="Enter License Number"
                            disabled={loading}
                        />
                    </div>
                </div>

                <div className="actions">
                    <button onClick={() => handleSearch(1)} disabled={loading}>
                        {loading ? "Searching..." : "Search"}
                    </button>
                    <button onClick={clearForm} disabled={loading}>
                        Clear
                    </button>
                </div>
            </div>

            {loading && <div className="loading-indicator">Loading...</div>}

            {results.length > 0 && (
                <div className="search-results">
                    <h3>Search Results ({totalResults} records found)</h3>
                    
                    <table className="results-table">
                        <thead>
                            <tr>
                                <th>NPI</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Specialty</th>
                                <th>State</th>
                                <th>City</th>
                                <th>Address</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((item, index) => (
                                <React.Fragment key={item.NPI}>
                                    <tr>
                                        <td>{item.NPI}</td>
                                        <td>{item.HCP_first_name}</td>
                                        <td>{item.HCP_last_name}</td>
                                        <td>{item.sp_2}</td>
                                        <td>{item.practice_st}</td>
                                        <td>{item.practice_city}</td>
                                        <td>{item.practice_address}</td>
                                        <td>
                                            <button
                                                onClick={() => handleView(item, index)}
                                                disabled={loading}
                                            >
                                                {popupRowIndex === index ? "Hide" : "View"}
                                            </button>
                                        </td>
                                    </tr>
                                    {popupRowIndex === index && selectedRecord && (
                                        <tr className="details-row">
                                            <td colSpan="8">
                                                <div className="details-content">
                                                    <h4>Detailed Information</h4>
                                                    <div className="details-grid">
                                                        <div>
                                                            <p><strong>Practice Address:</strong> {selectedRecord.practice_address || "N/A"}</p>
                                                            <p><strong>Mailing Address:</strong> {selectedRecord.mailing_address || "N/A"}</p>
                                                            <p><strong>Mailing City:</strong> {selectedRecord.mailing_city || "N/A"}</p>
                                                            <p><strong>Mailing State:</strong> {selectedRecord.mailing_st || "N/A"}</p>
                                                        </div>
                                                        <div>
                                                            <p><strong>License Number:</strong> {selectedRecord.Provider_License_Number || "N/A"}</p>
                                                            <p><strong>Provider License State:</strong> {selectedRecord.Provider_License_Number_State_Code || "N/A"}</p>
                                                            {/* <p><strong>Reason:</strong> {selectedRecord.Reason || "N/A"}</p> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="pagination-controls">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || loading}
                            >
                                Previous
                            </button>
                            <span>
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || loading}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HcpSearchApp;